// Paired DNA + RNA on the analysis list.
//
// The list payload carries the pairing on both sides (when the backend sends
// it; absent fields leave the list exactly as before):
//   DNA row: paired_rna     [{ id, display_sample_name, status, primary }]
//   RNA row: paired_dna_ids [ids]
// A paired patient reads as one row, the DNA row, with the RNA result appended
// to its Result & Actions. The list is paged on the server, so folding happens
// within the page: an RNA row is left out only when one of its DNA rows is on
// the same page. An RNA whose DNA is on another page, hidden or filtered out
// stays listed, so no analysis ever becomes unreachable from the list.

export const isRnaRow = (row) => Boolean(row?.tags?.includes('RNA'))

// The paired RNA analyses of a DNA row, the primary one first.
export const pairedRnaOf = (row) => {
  if (isRnaRow(row) || !Array.isArray(row?.paired_rna)) return []
  const valid = row.paired_rna.filter((rna) => rna && rna.id !== undefined && rna.id !== null)
  return [...valid].sort((a, b) => Number(Boolean(b.primary)) - Number(Boolean(a.primary)))
}

export const pairedDnaIdsOf = (row) =>
  isRnaRow(row) && Array.isArray(row?.paired_dna_ids) ? row.paired_dna_ids.filter((id) => id !== null) : []

// Rows of one page with every paired RNA row folded into its DNA row.
export const foldPairedRnaRows = (rows) => {
  if (!Array.isArray(rows)) return []
  const pageIds = new Set(rows.map((row) => String(row?.analysis_id)))
  const rnaShownUnderDna = new Set()
  rows.forEach((row) => pairedRnaOf(row).forEach((rna) => rnaShownUnderDna.add(String(rna.id))))
  const folded = rows.filter((row) => {
    if (!isRnaRow(row)) return true
    if (rnaShownUnderDna.has(String(row.analysis_id))) return false
    return !pairedDnaIdsOf(row).some((id) => pageIds.has(String(id)))
  })
  return folded.length === rows.length ? rows : folded
}

// Until the list payload carries the pairing, it is read per RNA row on the
// page from that RNA's reconciliation summary (usePairedRnaFallback). These
// helpers turn those answers into the same two fields.

// Whether the page already carries the pairing fields, so no lookups are needed.
export const pageHasPairingFields = (rows) =>
  Array.isArray(rows) && rows.some((row) => row && ('paired_rna' in row || 'paired_dna_ids' in row))

// RNA rows of the page whose pairing is to be looked up.
export const rnaIdsToLookUp = (rows) =>
  pageHasPairingFields(rows) ? [] : (rows || []).filter(isRnaRow).map((row) => row.analysis_id)

// The DNA analyses one RNA is paired with, from its reconciliation summary:
// `pairs[]` first, else the linked DNA analyses in `analyses[]`.
export const dnaPartnersFromReconciliation = (rnaId, summary) => {
  if (!summary || typeof summary !== 'object') return []
  const pairs = Array.isArray(summary.pairs) ? summary.pairs : []
  const fromPairs = pairs
    .filter((pair) => pair && pair.dna_analysis_id != null && String(pair.rna_analysis_id) === String(rnaId))
    .map((pair) => ({ dnaId: pair.dna_analysis_id, primary: Boolean(pair.primary) }))
  if (fromPairs.length > 0) return fromPairs
  const analyses = Array.isArray(summary.analyses) ? summary.analyses : []
  return analyses
    .filter((a) => a && a.id != null && a.basis !== 'self' && Array.isArray(a.analytes) && a.analytes.includes('dna'))
    .map((a) => ({ dnaId: a.id, primary: false }))
}

// The page with the looked-up pairing written into the two list fields.
// `partnersByRnaId` maps an RNA analysis id to its DNA partners; an RNA with no
// answer (pending or failed) is left as it is, so its row shows as today.
export const withLookedUpPairing = (rows, partnersByRnaId) => {
  if (!Array.isArray(rows) || !partnersByRnaId || Object.keys(partnersByRnaId).length === 0) return rows
  const rnaUnderDna = {}
  const out = rows.map((row) => {
    if (!isRnaRow(row)) return row
    const partners = partnersByRnaId[String(row.analysis_id)]
    if (!partners || partners.length === 0) return row
    partners.forEach(({ dnaId, primary }) => {
      const key = String(dnaId)
      rnaUnderDna[key] = rnaUnderDna[key] || []
      rnaUnderDna[key].push({
        id: row.analysis_id,
        display_sample_name: row.display_sample_name || row.sample_name,
        status: row.status,
        primary,
      })
    })
    return { ...row, paired_dna_ids: partners.map((p) => p.dnaId) }
  })
  return out.map((row) => {
    const rnas = !isRnaRow(row) && rnaUnderDna[String(row?.analysis_id)]
    return rnas ? { ...row, paired_rna: rnas } : row
  })
}
